<script setup>
const title = "Jak powstaje Twoja strona – 4 etapy współpracy z TurboWeb";
const description =
  "Zobacz, jak tworzymy strony: od prostych pytań, przez pisanie treści i projekt graficzny, aż po szybkie wdrożenie. Jasny proces bez chaosu i niespodzianek. Twój biznes, nasza technologia.";
useSeoMeta({
  title,
  description,
});
</script>

<template>
  <section id="proces" class="relative py-24">
    <!-- Decorative background -->
    <div class="absolute inset-0 -z-10 overflow-hidden">
      <div
        class="absolute -top-40 -right-40 size-[520px] rounded-full bg-gradient-to-tr from-fuchsia-500/30 via-indigo-500/20 to-cyan-400/20 blur-3xl"
      ></div>
      <div
        class="absolute -bottom-40 -left-40 size-[520px] rounded-full bg-gradient-to-tr from-cyan-400/20 via-sky-500/20 to-violet-600/25 blur-3xl"
      ></div>
      <div
        class="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.08),transparent_60%)]"
      ></div>
      <div
        class="absolute inset-0 [mask-image:linear-gradient(to_bottom,black,transparent_85%)]"
      >
        <div
          class="mx-auto size-full max-w-6xl bg-[linear-gradient(0deg,transparent_24px,rgba(255,255,255,0.06)_24px),linear-gradient(90deg,transparent_24px,rgba(255,255,255,0.06)_24px)] [background-size:64px_64px] opacity-40"
        ></div>
      </div>
    </div>

    <div class="mx-auto max-w-6xl px-6">
      <!-- Header -->
      <div class="mx-auto max-w-3xl text-center">
        <p
          class="text-xs font-semibold uppercase tracking-[0.2em] text-iq-paragraph-color/60"
        >
          Proces
        </p>
        <h2
          class="mt-2 text-4xl font-bold leading-tight tracking-tight text-iq-paragraph-color sm:text-5xl"
        >
          Jak pracujemy
        </h2>
        <p class="mt-4 text-iq-paragraph-color/70">
          Przejrzysty, 4‑etapowy proces od planu do wdrożenia — szybko, prosto i
          konkretnie.
        </p>
      </div>

      <ol class="mt-14 grid gap-6 sm:grid-cols-2 lg:gap-8">
        <li>
          <StepCard
            :num="1"
            title="Strategia – zaczynamy od rozmowy i planu"
            text="Zanim usiądziemy do komputera, chcemy Cię dobrze poznać. Pytamy o Twoją firmę, czym się zajmujesz, kto jest Twoim klientem i co chcesz osiągnąć. Patrzymy też, co robi Twoja konkurencja i jak można się wyróżnić. To nie jest trudne ani nudne – wystarczy kilka prostych pytań. Na podstawie tych informacji planujemy, jak najlepiej pokazać Twoją firmę w internecie."
            effect="Efekt: jasny plan działania"
          >
            <template #icon>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                class="size-6"
              >
                <path d="M7 8h10M7 12h6" />
                <path d="M4 6a3 3 0 0 1 3-3h10a3 3 0 0 1 3 3v7a3 3 0 0 1-3 3H9l-4 4V6Z" />
              </svg>
            </template>
          </StepCard>
        </li>

        <li>
          <StepCard
            :num="2"
            title="Treści – konkretne i dopasowane do klientów"
            text="Na bazie strategii piszemy teksty na stronę. Prosto, jasno i z myślą o ludziach, którzy mają ją odwiedzić. Treści są napisane specjalnie pod Twoją ofertę, bez lania wody i przypadkowych frazesów. Klient od razu wie, czym się zajmujesz i co może od Ciebie dostać."
            effect="Efekt: klarowne komunikaty"
          >
            <template #icon>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                class="size-6"
              >
                <path d="M9 7h3m-3 4h6m-6 4h6" />
                <path d="M7 3h7l5 5v11a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z" />
              </svg>
            </template>
          </StepCard>
        </li>

        <li>
          <StepCard
            :num="3"
            title="Design – wygląd, który ma sens"
            text="Kiedy treści są gotowe, projektujemy stronę. Wszystko — kolory, układ, przyciski, zdjęcia — dobieramy tak, by strona była nowoczesna, spójna i łatwa w obsłudze. Zależy nam, żeby użytkownik szybko znalazł to, czego szuka — i zrobił to, na czym Ci zależy: zadzwonił, kupił, zapytał."
            effect="Efekt: estetyczny, funkcjonalny projekt"
          >
            <template #icon>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                class="size-6"
              >
                <rect x="3" y="4" width="18" height="16" rx="2" />
                <path d="M9 4v16M3 10h18" />
              </svg>
            </template>
          </StepCard>
        </li>

        <li>
          <StepCard
            :num="4"
            title="Wdrożenie – strona gotowa do działania"
            text="Na końcu składamy wszystko w całość. Programujemy stronę, ustawiamy wersje na komputer i telefon, optymalizujemy pod wyszukiwarki. Strona działa szybko, wygląda dobrze i jest gotowa, żeby zacząć pracować."
            effect="Efekt: szybkie, stabilne wdrożenie"
          >
            <template #icon>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                class="size-6"
              >
                <path
                  d="M14 10c-1.5 1.5-4 2-6 2 .2-2 1-4.5 2.5-6S14 3 16 3c0 2-1 5-2 7Z"
                />
                <path d="M8 12s-1 4-5 6c2-4 6-5 6-5M13 13l3 3" />
                <path d="M13 13s-.5 2-2 3-3 2-3 2" />
              </svg>
            </template>
          </StepCard>
        </li>
      </ol>
    </div>
  </section>
</template>
